// StackedCards.jsx
"use client";

import React from "react";
import {
  Box,
  Flex,
  Text,
  Badge,
  HStack,
  VStack,
  Tag,
  Button,
  chakra,
  shouldForwardProp,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import { AnimatePresence, isValidMotionProp, motion } from "framer-motion";

const MotionBox = chakra(motion.div, {
  shouldForwardProp: (prop) => isValidMotionProp(prop) || shouldForwardProp(prop),
});

const pick = (arr, fallback = []) => (Array.isArray(arr) ? arr : fallback);

const getCover = (p) => p?.coverImage || p?.modalImages?.[0] || p?.images?.[0] || "";

const toCard = (p, idx) => {
  const techTitles = pick(p.techStack).map((t) => t?.title).filter(Boolean);
  const apiTitles = pick(p.externalAPIs).map((a) => a?.title).filter(Boolean);

  return {
    id: p.id ?? p._id ?? `${p.title || "project"}-${idx}`,
    title: p.title || "Untitled",
    category: p.category || "Project",
    desc: p.description || "",
    modalDescription: p.modalDescription || p.description || "",
    cover: getCover(p),
    techTitles,
    apiTitles,
    featured: !!p.featured,
    raw: p,
  };
};

export default function StackedCards({
  projects = [],
  maxCards = 5,
  // optional: pass a handler if you want a modal open on button click
  onCardClick,
}) {
  // ✅ theme vars (defined once)
  const surfaceBg = useColorModeValue("white", "rgba(255,255,255,0.06)");
  const borderColor = useColorModeValue("blackAlpha.200", "whiteAlpha.200");
  const mutedText = useColorModeValue("blackAlpha.700", "whiteAlpha.700");
  const hintText = useColorModeValue("blackAlpha.500", "whiteAlpha.500");
  const tagBg = useColorModeValue("blackAlpha.50", "whiteAlpha.100");
  const emptyBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50");

  const list = React.useMemo(() => {
    const arr = pick(projects)
      .filter(Boolean)
      // prefer featured first, then rest
      .sort((a, b) => Number(!!b.featured) - Number(!!a.featured))
      .slice(0, maxCards)
      .map(toCard);

    return arr;
  }, [projects, maxCards]);

  const [hoverId, setHoverId] = React.useState(null);
  const [pinnedId, setPinnedId] = React.useState(null);

  const activeId = pinnedId || hoverId;
  const active = list.find((c) => c.id === activeId) || list[0] || null;

  const layoutDir = useBreakpointValue({ base: "column", md: "row" });

  // Regular horizontal stack geometry (row-side)
  const cardW = useBreakpointValue({ base: 360, md: 440 }) || 440;
  const cardH = useBreakpointValue({ base: 300, md: 330 }) || 330;

  // each deeper card shifts right by this much (clean + regular)
  const tabW = useBreakpointValue({ base: 56, md: 70 }) || 70;

  // tiny depth so it feels stacked (still horizontal)
  const depthY = useBreakpointValue({ base: 8, md: 10 }) || 10;

  const deckW = cardW + Math.max(0, list.length - 1) * tabW;
  const deckH = cardH + Math.max(0, list.length - 1) * depthY;

  const togglePin = (id) => {
    setPinnedId((prev) => (prev === id ? null : id));
    setHoverId(id);
  };

  if (!list.length) {
    return (
      <Box
        border="1px dashed"
        borderColor={borderColor}
        bg={emptyBg}
        borderRadius="2xl"
        p={8}
      >
        <Text fontWeight="800">No projects found</Text>
        <Text mt={2} color={mutedText}>
          Pass <code>projects</code> into StackedCards.
        </Text>
      </Box>
    );
  }

  return (
    <Flex w="full" justify="center" py={{ base: 4, md: 6 }}>
      <Flex
        direction={layoutDir}
        gap={{ base: 6, md: 10 }}
        w="full"
        maxW="1100px"
        align="flex-start"
      >
        {/* LEFT: Regular horizontal stack */}
        <Box
          position="relative"
          w={`${deckW}px`}
          h={`${deckH}px`}
          onMouseLeave={() => {
            if (!pinnedId) setHoverId(null);
          }}
        >
          {/* Glow */}
          <Box
            position="absolute"
            inset="-18px"
            borderRadius="28px"
            bgGradient="linear(to-br, rgba(59,130,246,0.16), rgba(168,85,247,0.12), rgba(244,63,94,0.08))"
            filter="blur(18px)"
            opacity={activeId ? 1 : 0.75}
            pointerEvents="none"
          />

          {/* VISUAL CARDS only (no pointer events => no messy hover cascade) */}
          {list.map((c, i) => {
            const baseX = i * tabW;
            const baseY = i * depthY;
            const isActive = c.id === activeId;

            return (
              <MotionBox
                key={c.id}
                position="absolute"
                top="0"
                left="0"
                w={`${cardW}px`}
                h={`${cardH}px`}
                borderRadius="22px"
                overflow="hidden"
                bg={surfaceBg}
                border="1px solid"
                borderColor={borderColor}
                style={{
                  zIndex: isActive ? 999 : 100 - i,
                  pointerEvents: "none",
                  willChange: "transform",
                }}
                animate={{
                  x: baseX,
                  y: isActive ? baseY - 14 : baseY,
                  scale: isActive ? 1.02 : 1,
                  opacity: activeId && !isActive ? 0.9 : 1,
                }}
                transition={{ type: "spring", stiffness: 340, damping: 28 }}
                boxShadow={
                  isActive
                    ? "0 28px 90px rgba(0,0,0,0.30)"
                    : "0 14px 45px rgba(0,0,0,0.16)"
                }
              >
                {/* Header image */}
                <Box h="110px" position="relative">
                  {c.cover ? (
                    <Box
                      as="img"
                      src={c.cover}
                      alt={c.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                      loading="lazy"
                    />
                  ) : (
                    <Box
                      position="absolute"
                      inset="0"
                      bgGradient="linear(to-br, #0ea5e9, #a78bfa, #fb7185)"
                    />
                  )}

                  <Box
                    position="absolute"
                    inset="0"
                    bgGradient="linear(to-b, rgba(0,0,0,0.10), rgba(0,0,0,0.58))"
                  />

                  <Box position="absolute" left="16px" bottom="14px">
                    <HStack spacing={2} align="center">
                      <Text fontSize="lg" fontWeight="900" color="white" letterSpacing="-0.02em">
                        {c.title}
                      </Text>
                      {c.featured && (
                        <Badge
                          bg="whiteAlpha.300"
                          color="white"
                          borderRadius="full"
                          px={3}
                          py={1}
                          fontSize="0.75em"
                        >
                          Featured
                        </Badge>
                      )}
                    </HStack>
                    <Text fontSize="sm" color="whiteAlpha.800">
                      {c.category}
                    </Text>
                  </Box>
                </Box>

                {/* Body */}
                <Box p={5}>
                  <HStack spacing={2} flexWrap="wrap">
                    {c.techTitles.slice(0, 4).map((t) => (
                      <Tag
                        key={t}
                        size="sm"
                        borderRadius="full"
                        bg={tagBg}
                        border="1px solid"
                        borderColor={borderColor}
                        fontWeight="700"
                      >
                        {t}
                      </Tag>
                    ))}
                  </HStack>

                  <Text mt={3} fontSize="sm" color={mutedText} noOfLines={2}>
                    {c.desc}
                  </Text>

                  <Text mt={3} fontSize="xs" color={hintText}>
                    Hover the right tabs • Click a tab to pin
                  </Text>
                </Box>
              </MotionBox>
            );
          })}

          {/* HIT-ZONES: right-side tabs only (clean hover) */}
          <Box position="absolute" inset="0" zIndex={2000}>
            {/* front area selects first card (optional) */}
            <Box
              position="absolute"
              top="0"
              left="0"
              w={`${Math.max(0, cardW - tabW)}px`}
              h={`${deckH}px`}
              cursor="pointer"
              onMouseEnter={() => !pinnedId && setHoverId(list[0].id)}
              onClick={() => togglePin(list[0].id)}
            />

            {/* tab strips for cards 1..n-1 */}
            {list.slice(1).map((c, idx) => {
              const i = idx + 1;
              const x = cardW + (i - 1) * tabW;

              const isPinned = pinnedId === c.id;
              const isActive = activeId === c.id;

              return (
                <Box
                  key={c.id}
                  position="absolute"
                  top="0"
                  left={`${x}px`}
                  w={`${tabW}px`}
                  h={`${deckH}px`}
                  cursor="pointer"
                  onMouseEnter={() => !pinnedId && setHoverId(c.id)}
                  onClick={() => togglePin(c.id)}
                >
                  <Box
                    position="absolute"
                    top="16px"
                    left="50%"
                    transform="translateX(-50%)"
                    w="10px"
                    h="10px"
                    borderRadius="full"
                    bg={isPinned ? "whiteAlpha.900" : isActive ? "whiteAlpha.700" : "whiteAlpha.400"}
                    opacity={0.95}
                    mixBlendMode="overlay"
                  />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* RIGHT: Spotlight (uses API modalDescription + APIs) */}
        <Box flex="1" minH={{ base: "260px", md: "360px" }}>
          <AnimatePresence mode="wait">
            {active ? (
              <MotionBox
                key={active.id}
                initial={{ opacity: 0, y: 12, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.99 }}
                transition={{ type: "spring", stiffness: 260, damping: 24 }}
                borderRadius="24px"
                border="1px solid"
                borderColor={borderColor}
                bg={surfaceBg}
                overflow="hidden"
                boxShadow="0 26px 80px rgba(0,0,0,0.18)"
              >
                <Box h="130px" position="relative">
                  {active.cover ? (
                    <Box
                      as="img"
                      src={active.cover}
                      alt={active.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      loading="lazy"
                    />
                  ) : (
                    <Box position="absolute" inset="0" bgGradient="linear(to-br, #0ea5e9, #a78bfa, #fb7185)" />
                  )}

                  <Box
                    position="absolute"
                    inset="0"
                    bgGradient="linear(to-b, rgba(0,0,0,0.10), rgba(0,0,0,0.62))"
                  />

                  <Box position="absolute" bottom="14px" left="18px">
                    <Text fontSize="2xl" fontWeight="900" color="white" letterSpacing="-0.02em">
                      {active.title}
                    </Text>
                    <Text fontSize="sm" color="whiteAlpha.800">
                      {active.category}
                    </Text>
                  </Box>
                </Box>

                <Box p={{ base: 5, md: 6 }}>
                  <HStack spacing={2} flexWrap="wrap">
                    {active.techTitles.slice(0, 8).map((t) => (
                      <Tag
                        key={t}
                        size="sm"
                        borderRadius="full"
                        bg={tagBg}
                        border="1px solid"
                        borderColor={borderColor}
                        fontWeight="700"
                      >
                        {t}
                      </Tag>
                    ))}
                  </HStack>

                  <Text mt={4} fontSize="md" color={mutedText} lineHeight="1.7">
                    {active.modalDescription}
                  </Text>

                  {active.apiTitles.length > 0 && (
                    <Box mt={4}>
                      <Text fontWeight="800" mb={2} color={mutedText}>
                        External APIs
                      </Text>
                      <HStack spacing={2} flexWrap="wrap">
                        {active.apiTitles.slice(0, 8).map((a) => (
                          <Tag
                            key={a}
                            size="sm"
                            borderRadius="full"
                            bg={tagBg}
                            border="1px solid"
                            borderColor={borderColor}
                            fontWeight="700"
                          >
                            {a}
                          </Tag>
                        ))}
                      </HStack>
                    </Box>
                  )}

                  <HStack mt={6} justify="space-between" flexWrap="wrap" gap={3}>
                    <Text fontSize="sm" color={hintText}>
                      {pinnedId ? "Pinned — click the same tab to unpin." : "Tip: click a tab to pin (mobile too)."}
                    </Text>

                    {typeof onCardClick === "function" && (
                      <Button
                        size="sm"
                        borderRadius="full"
                        fontWeight="800"
                        onClick={() => onCardClick(active.raw)}
                      >
                        View Project
                      </Button>
                    )}
                  </HStack>
                </Box>
              </MotionBox>
            ) : null}
          </AnimatePresence>
        </Box>
      </Flex>
    </Flex>
  );
}
